// src/finance/optimum.ts
// Part salaire optimale du mix (spec 2026-08-24 § V5). Fonction PURE : aucun
// état, aucune dépendance à React — le composant se charge de la mémoïser.
//
// Pas d'optimisation analytique : la courbe net(part) enchaîne un barème IR
// progressif, le marginal relief de la Corporation Tax et la décote, dont les
// ruptures de pente ne se dérivent pas proprement. Un balayage à pas fin est
// robuste, exact au pas près, et exactement ce que le curseur permet de régler
// (le curseur avance par crans de 5 %, le balayage par crans de 1 %).
import { simulateRegime } from './engine/engine'
import { findRevenueForNet } from './engine/invert'
import type { CorporateRegime, FinanceSettings } from './engine/types'

/** Pas par défaut : 101 simulations, soit la finesse du centième de part. */
const DEFAULT_STEP = 0.01

export type OptimumShare = {
  /** Part salaire optimale, dans [0, 1] et multiple exact du pas. */
  share: number
  /**
   * CA mensuel requis (direction `netToRevenue`) ou net mensuel obtenu
   * (direction `revenueToNet`) à cette part — la grandeur qui a été optimisée.
   */
  value: number
}

/**
 * Balaie la part salaire de 0 à 1 et rend celle qui optimise l'objectif courant :
 * le CA requis le PLUS BAS en `netToRevenue`, le net le PLUS HAUT en `revenueToNet`.
 *
 * Rend `null` uniquement si AUCUNE part n'atteint la cible — `findRevenueForNet`
 * rend `null` sur des paramètres confiscatoires, et une part inatteignable est
 * simplement écartée du balayage plutôt que de faire échouer l'ensemble.
 *
 * À égalité stricte, la plus petite part l'emporte : le résultat ne dépend donc
 * pas de l'ordre de parcours, et un jeu de paramètres sans aucun prélèvement ne
 * fait pas clignoter le repère d'un rendu à l'autre.
 */
export function optimalSalaryShare(
  settings: FinanceSettings,
  companyExpensesMonthly: number,
  corporateRegime: CorporateRegime = 'uk',
  step: number = DEFAULT_STEP,
): OptimumShare | null {
  // Un pas nul ou négatif ferait diverger le nombre d'itérations ; au-delà de 1
  // il ne resterait que les deux bornes. Dans les deux cas, repli sur le défaut.
  const usable = step > 0 && step <= 1 ? step : DEFAULT_STEP
  const steps = Math.max(1, Math.round(1 / usable))

  let best: OptimumShare | null = null
  for (let i = 0; i <= steps; i += 1) {
    // i / steps, et non une accumulation `share += step` : l'accumulation dérive
    // (0.1 + 0.2 ≠ 0.3) et ne retomberait jamais exactement sur 1.
    const share = i / steps
    if (settings.direction === 'netToRevenue') {
      const revenue = findRevenueForNet(
        settings.targetAmount,
        share,
        settings,
        companyExpensesMonthly,
        corporateRegime,
      )
      if (revenue === null) continue
      if (best === null || revenue < best.value) best = { share, value: revenue }
    } else {
      const net = simulateRegime(
        settings.targetAmount,
        share,
        settings,
        companyExpensesMonthly,
        corporateRegime,
      ).netMonthly
      if (best === null || net > best.value) best = { share, value: net }
    }
  }
  return best
}
